import {
  FREE_BOOKING_LISTING_TYPE,
  GROUP_SESSION_LISTING_TYPE,
  INDIVIDUAL_SESSION_LISTING_TYPE,
  PAYLATER_BOOKING_LISTING_TYPE,
} from '../config/configListing';
import { isCampaignInboxListing } from './practitionerCampaign';
import { isDigitalProductListingType } from './soulwise-marketplace/listingType';
import { normalizeLocale, DEFAULT_LOCALE } from './locale';

/**
 * Listing types we can fully clone (details + photos + availability).
 * Digital / Fastio content stays visible in the ⋮ menu but disabled —
 * Fastio assets cannot be safely cloned. Server still refuses those types.
 */
export const COPYABLE_LISTING_TYPES = [
  INDIVIDUAL_SESSION_LISTING_TYPE,
  GROUP_SESSION_LISTING_TYPE,
  FREE_BOOKING_LISTING_TYPE,
  PAYLATER_BOOKING_LISTING_TYPE,
];

export const COPY_LISTING_UNSAFE_REASON = {
  DIGITAL_CONTENT: 'digital_content',
  DIGITAL_MEDIA: 'digital_media',
  CAMPAIGN: 'campaign_inbox',
  UNSUPPORTED_TYPE: 'unsupported_listing_type',
  TRASHED: 'trashed',
};

export const COPY_TITLE_PREFIX_BY_LOCALE = {
  en: 'Copy of ',
  ar: 'نسخة من ',
};
export const COPY_TITLE_UNTITLED_BY_LOCALE = {
  en: 'listing',
  ar: 'الخدمة',
};
export const COPY_TITLE_PREFIX = COPY_TITLE_PREFIX_BY_LOCALE.en;
const COPY_TITLE_PREFIXES = Object.values(COPY_TITLE_PREFIX_BY_LOCALE);

const listingTypeOf = listing => listing?.attributes?.publicData?.listingType || null;

const hasDigitalMediaItems = listing => {
  const items = listing?.attributes?.publicData?.digitalMedia?.items;
  return Array.isArray(items) && items.length > 0;
};

const isTrashed = listing => !!listing?.attributes?.privateData?.trash?.trashedAt;

/**
 * Whether the Copy action should appear for this own listing.
 * Hide rather than produce a partial/broken copy.
 */
export const getListingCopyEligibility = listing => {
  if (!listing) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.UNSUPPORTED_TYPE };
  }
  if (isTrashed(listing)) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.TRASHED };
  }
  if (isCampaignInboxListing(listing)) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.CAMPAIGN };
  }
  const listingType = listingTypeOf(listing);
  if (isDigitalProductListingType(listingType)) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.DIGITAL_CONTENT };
  }
  if (hasDigitalMediaItems(listing)) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.DIGITAL_MEDIA };
  }
  if (!COPYABLE_LISTING_TYPES.includes(listingType)) {
    return { canCopy: false, reason: COPY_LISTING_UNSAFE_REASON.UNSUPPORTED_TYPE };
  }
  return { canCopy: true, reason: null };
};

export const canCopyListing = listing => getListingCopyEligibility(listing).canCopy;

const CONTENT_COPY_UNAVAILABLE_REASONS = new Set([
  COPY_LISTING_UNSAFE_REASON.DIGITAL_CONTENT,
  COPY_LISTING_UNSAFE_REASON.DIGITAL_MEDIA,
]);

/**
 * Card ⋮ menu state. Copyable types are enabled. Digital content and Fastio
 * media show a disabled Copy row with an always-visible note. Campaign inbox,
 * Trash, and unknown types stay hidden.
 */
export const getListingCopyMenuState = listing => {
  const { canCopy, reason } = getListingCopyEligibility(listing);
  if (canCopy) {
    return { show: true, disabled: false, reason: null };
  }
  if (CONTENT_COPY_UNAVAILABLE_REASONS.has(reason)) {
    return { show: true, disabled: true, reason };
  }
  return { show: false, disabled: true, reason };
};

export const copyListingErrorMessageId = error => {
  const code = error?.code || error?.data?.code;
  if (code === 'listing_copy_unsupported') {
    return 'ManageListingCard.copyListingUnsupported';
  }
  return 'ManageListingCard.copyListingFailed';
};

export const copyListingErrorMessageDescriptor = error => {
  if (copyListingErrorMessageId(error) === 'ManageListingCard.copyListingUnsupported') {
    return {
      id: 'ManageListingCard.copyListingUnsupported',
      defaultMessage: 'This listing type cannot be copied.',
    };
  }
  return {
    id: 'ManageListingCard.copyListingFailed',
    defaultMessage: 'Could not copy this listing. Please try again.',
  };
};

export const formatCopyListingErrorMessage = (intl, error) =>
  intl.formatMessage(copyListingErrorMessageDescriptor(error));

export const copyListingTitle = (title, locale) => {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  const prefix = COPY_TITLE_PREFIX_BY_LOCALE[normalizedLocale] || COPY_TITLE_PREFIX_BY_LOCALE.en;
  const untitled =
    COPY_TITLE_UNTITLED_BY_LOCALE[normalizedLocale] || COPY_TITLE_UNTITLED_BY_LOCALE.en;
  const trimmed = typeof title === 'string' ? title.trim() : '';
  if (!trimmed) {
    return `${prefix}${untitled}`;
  }
  if (COPY_TITLE_PREFIXES.some(existing => trimmed.startsWith(existing))) {
    return trimmed;
  }
  return `${prefix}${trimmed}`;
};
